import type { CompRef, MenuItem } from '@wix/document-services-types';
import { DATA } from '@wix/component-protocol/schema';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import { ManifestResolver, VirtualManifestAPI } from '../api';
import { ExternalPropsHandler } from '../handlers/ExternalPropsHandler';
import type { EditorPointer } from '@wix/editor-document-logic-api';

interface ISelectedMenuItemPathEntry {
  path: string[];
  editorPointer: EditorPointer<CompRef>;
}

interface ISelectableMenuItem extends Omit<MenuItem, 'items'> {
  selected?: boolean;
  items?: ISelectableMenuItem[];
}

interface ISelectableMenuItemOverride {
  selected?: boolean;
  items?: ISelectableMenuItemOverride[];
}

const GLOBAL_SELECTED_MENU_ITEM_PATH_MAP = new Map<
  string,
  ISelectedMenuItemPathEntry
>();

export class MenuItemsPlatformService extends AbstractComponentsPlatformService {
  #propsHandler: ExternalPropsHandler;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.#propsHandler =
      this.virtualElementProvider.getManifestQueryAPI().props;
  }

  #getEntryKey(editorPointer: EditorPointer<CompRef>) {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return this.editorPointerAPI.getEditorPointerUniqueId(
      documentPointer,
      innerElementPath,
      { ignoreVariants: true },
    );
  }

  #findMenuItemsKey(resolver: ManifestResolver) {
    const dataDefinitions = resolver.getDataDefinitions();

    const menuItemsDataItems = Object.entries(dataDefinitions ?? {}).filter(
      ([_dataItemKey, dataItemValue]) =>
        dataItemValue.dataType === DATA.DATA_TYPE.menuItems,
    );

    if (!menuItemsDataItems.length || menuItemsDataItems.length > 1) {
      return undefined;
    }

    return menuItemsDataItems[0][0];
  }

  #getMenuItemsByKey(api: VirtualManifestAPI, menuItemsKey: string) {
    const props = this.#propsHandler.getProps(
      api.getEditorPointer(),
      api.getExtensionType(),
    );
    const menuItems = props?.[menuItemsKey] as ISelectableMenuItem[];

    if (!menuItems) {
      return null;
    }

    return menuItems;
  }

  getMenuItems(api: VirtualManifestAPI) {
    const menuItemsKey = this.#findMenuItemsKey(api.resolver);

    if (!menuItemsKey) {
      return undefined;
    }

    const menuItems = this.#getMenuItemsByKey(api, menuItemsKey);

    if (!menuItems) {
      return undefined;
    }

    return menuItems;
  }

  getSelectedMenuItemPath(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return undefined;
    }

    const editorPointer = api.getEditorPointer();

    const selectedMenuItemPathEntry = GLOBAL_SELECTED_MENU_ITEM_PATH_MAP.get(
      this.#getEntryKey(editorPointer),
    );

    return selectedMenuItemPathEntry?.path;
  }

  #buildSelectionOverride(
    menuItems: ISelectableMenuItem[],
    path: string[],
  ): ISelectableMenuItemOverride[] {
    if (!path.length) {
      return [];
    }
    const [firstId, ...rest] = path;
    const index = menuItems.findIndex((item) => item.id === firstId);
    if (index === -1) {
      throw new Error(`Invalid menu item id: ${firstId}`);
    }

    const entry: ISelectableMenuItemOverride = { selected: true };

    if (rest.length) {
      const childItems = menuItems[index].items;
      if (!childItems) {
        throw new Error(`Menu item ${firstId} has no nested items`);
      }
      entry.items = this.#buildSelectionOverride(childItems, rest);
    }

    const result: ISelectableMenuItemOverride[] = [];
    result[index] = entry;
    return result;
  }

  setSelectedMenuItemPath(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
    path: string[],
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return;
    }

    const editorPointer = api.getEditorPointer();

    if (!Array.isArray(path) || path.some((p) => typeof p !== 'string')) {
      return;
    }

    const menuItemsKey = this.#findMenuItemsKey(api.resolver);

    if (!menuItemsKey) {
      return;
    }

    const menuItems = this.getMenuItems(api);
    if (!menuItems) {
      return;
    }

    this.#propsHandler.removePropsOverrides(
      editorPointer,
      api.getExtensionType(),
      [menuItemsKey],
    );

    const itemsOverride = this.#buildSelectionOverride(menuItems, path);

    GLOBAL_SELECTED_MENU_ITEM_PATH_MAP.set(this.#getEntryKey(editorPointer), {
      path,
      editorPointer,
    });

    this.#propsHandler.updatePropsOverrides(
      editorPointer,
      api.getExtensionType(),
      { [menuItemsKey]: itemsOverride },
    );
  }

  resetSelectedMenuItemPath(
    apiOrEditorPointers:
      | VirtualManifestAPI
      | EditorPointer<CompRef>
      | EditorPointer<CompRef>[],
  ) {
    const api =
      apiOrEditorPointers instanceof VirtualManifestAPI
        ? apiOrEditorPointers
        : this.extractVirtualElement(apiOrEditorPointers).getAPI();

    if (api.isMultipleComponentsRoot()) {
      return;
    }

    const editorPointer = api.getEditorPointer();

    const previousPath = this.getSelectedMenuItemPath(editorPointer);
    if (!previousPath?.length) {
      return;
    }

    const menuItemsKey = this.#findMenuItemsKey(api.resolver);
    if (!menuItemsKey) {
      return;
    }

    GLOBAL_SELECTED_MENU_ITEM_PATH_MAP.delete(this.#getEntryKey(editorPointer));

    this.#propsHandler.removePropsOverrides(
      editorPointer,
      api.getExtensionType(),
      [menuItemsKey],
    );
  }
}
